import clsx from "clsx";
import { observer } from "mobx-react";
import { FunctionComponent, useCallback } from "react";

import Timestamp from "@ui/components/meta/Timestamp/Timestamp";
import Avatar, { AvatarSize } from "@ui/components/notifications/Avatar/Avatar";
import CommunityLogo from "@ui/components/notifications/CommunityLogo/CommunityLogo";
import {
  useCommunityContext,
  type CycloneCommunities,
} from "@voxmedia/duet-context/context/community-context";
import {
  AnyRecNotification,
  AnyRecSummaryNotification,
  AnyReplyNotification,
  CoralCommentWasApprovedNotification,
  CoralReplyNotification,
  CoralRecNotification,
  CoralRecSummaryNotification,
  CycloneFanPostRecNotification,
  CycloneFanPostRecSummaryNotification,
  CycloneFanPostReplyNotification,
  CycloneFanPostWasApprovedNotification,
  CycloneReplyNotification,
  CycloneReplyWasApprovedNotification,
  ExternalUserProfile,
  AnyNotification,
  NotificationType,
  CoralStory,
  CycloneRootAncestor,
} from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./Notification.css";

export const getUrlForNotification = (
  notification: AnyNotification,
  cycloneCommunitiesById: CycloneCommunities,
): string => {
  let url = "";
  let cycloneCommunityId: string | undefined;

  switch (notification.type) {
    case NotificationType.CycloneFanPostReply:
    case NotificationType.CycloneReply:
    case NotificationType.CycloneReplyRec:
    case NotificationType.CycloneReplyRecSummary:
      url = (notification as CycloneReplyNotification).reply.url;
      cycloneCommunityId = (notification as CycloneReplyNotification).community
        .id;
      break;
    case NotificationType.CycloneFanPostRec:
    case NotificationType.CycloneFPRecSummary:
      url = (
        notification as
          | CycloneFanPostRecNotification
          | CycloneFanPostRecSummaryNotification
      ).fanPost.url;
      cycloneCommunityId = (
        notification as
          | CycloneFanPostRecNotification
          | CycloneFanPostRecSummaryNotification
      ).community.id;
      break;
    case NotificationType.CycloneFanPostApproved:
      url = (notification as CycloneFanPostWasApprovedNotification).fanPost.url;
      cycloneCommunityId = (
        notification as CycloneFanPostWasApprovedNotification
      ).community.id;
      break;
    case NotificationType.CycloneReplyApproved:
      url = (notification as CycloneReplyWasApprovedNotification).reply.url;
      cycloneCommunityId = (notification as CycloneReplyWasApprovedNotification)
        .community.id;
      break;
    case NotificationType.CoralReply:
      return (notification as CoralReplyNotification).reply.url;
    case NotificationType.CoralRec:
    case NotificationType.CoralCommentApproved:
      return (
        notification as
          | CoralRecNotification
          | CoralCommentWasApprovedNotification
      ).comment.url;
    case NotificationType.CoralRecSummary:
      return (notification as CoralRecSummaryNotification).comment.url;
    default:
      return "";
  }

  const postPrefix =
    cycloneCommunityId &&
    cycloneCommunitiesById[cycloneCommunityId]?.cycloneCommunityPrefix;
  if (!postPrefix) {
    return url;
  }

  const prefixedPostPath = `${postPrefix}/post/`;
  return url.includes(prefixedPostPath)
    ? url
    : url.replace("/post/", prefixedPostPath);
};

const getTextForNotification = (
  notification: AnyNotification,
  rootAncestor: CoralStory | CycloneRootAncestor | null,
): string => {
  switch (notification.type) {
    case NotificationType.CycloneReply:
    case NotificationType.CoralReply:
      if (rootAncestor && "title" in rootAncestor) {
        return `replied to your comment on "${rootAncestor.title}"`;
      } else {
        return "replied to your comment";
      }
    case NotificationType.CycloneReplyRec:
    case NotificationType.CoralRec:
      if (rootAncestor && "title" in rootAncestor) {
        return `rec'd your comment on "${rootAncestor.title}"`;
      } else {
        return "rec'd your comment";
      }
    case NotificationType.CycloneFanPostReply:
      if (rootAncestor && "title" in rootAncestor) {
        return `replied to your post "${rootAncestor.title}"`;
      } else {
        return "replied to your post";
      }
    case NotificationType.CycloneFanPostRec:
      if (rootAncestor && "title" in rootAncestor) {
        return `rec'd your post on "${rootAncestor.title}"`;
      } else {
        return "rec'd your post";
      }
    case NotificationType.CoralRecSummary:
    case NotificationType.CycloneReplyRecSummary:
      return `rec'd your comment`;
    case NotificationType.CycloneFPRecSummary:
      if (rootAncestor && "title" in rootAncestor) {
        return `rec'd your post "${rootAncestor.title}"`;
      } else {
        return "rec'd your post";
      }
    case NotificationType.CoralCommentApproved:
    case NotificationType.CycloneReplyApproved:
      return "Your comment has been published.";
    case NotificationType.CycloneFanPostApproved:
      return "Your post has been published.";
    default:
      return "";
  }
};

const getFromProfileForRecs = (notification: AnyRecSummaryNotification) => {
  const recs = notification.recs as AnyRecNotification[];
  if (recs.length > 0) {
    return recs.map((rec) => rec.from);
  }
  return null;
};

const getFromProfileForNotification = (notification: AnyNotification) => {
  switch (notification.type) {
    case NotificationType.CycloneReplyRecSummary:
    case NotificationType.CycloneFPRecSummary:
    case NotificationType.CoralRecSummary:
      return getFromProfileForRecs(notification as AnyRecSummaryNotification);
    default:
      return (notification as AnyRecNotification | AnyReplyNotification).from;
  }
};

const getRootAncestorForNotification = (notification: AnyNotification) => {
  switch (notification.type) {
    case NotificationType.CycloneReply:
    case NotificationType.CycloneReplyRec:
    case NotificationType.CycloneReplyRecSummary:
      return (
        (notification as CycloneReplyNotification).rootAncestor ??
        (notification as CycloneReplyNotification).fanPost
      );
    case NotificationType.CycloneFanPostReply:
    case NotificationType.CycloneFanPostRec:
    case NotificationType.CycloneFPRecSummary:
      return (notification as CycloneFanPostReplyNotification).fanPost;
    case NotificationType.CoralReply:
    case NotificationType.CoralRec:
    case NotificationType.CoralRecSummary:
    case NotificationType.CoralCommentApproved:
      return (notification as CoralReplyNotification).story;
    default:
      return null;
  }
};

const getSnippetForNotification = (notification: AnyNotification): string => {
  switch (notification.type) {
    case NotificationType.CoralRecSummary:
    case NotificationType.CoralCommentApproved:
      return (notification as CoralRecSummaryNotification).comment.snippet;
    case NotificationType.CycloneFanPostApproved:
      return (notification as CycloneFanPostWasApprovedNotification).fanPost
        .snippet;
    case NotificationType.CycloneReplyApproved:
      return (notification as CycloneReplyWasApprovedNotification).reply
        .snippet;
    default:
      return "reply" in notification
        ? (notification as AnyReplyNotification).reply.snippet
        : "";
  }
};

const getFromProfileText = (
  from: ExternalUserProfile[] | ExternalUserProfile,
  type: NotificationType,
) => {
  if (
    type === NotificationType.CoralCommentApproved ||
    type === NotificationType.CycloneFanPostApproved ||
    type === NotificationType.CycloneReplyApproved
  ) {
    return "";
  }

  if (Array.isArray(from)) {
    if (from.length > 0) {
      if (from.length === 1) {
        return <span className={styles.username}>{from[0]?.username}</span>;
      } else if (from.length === 2) {
        return (
          <>
            <span className={styles.username}>{from[0]?.username}</span> and{" "}
            <span className={styles.username}>{from[1]?.username}</span>
          </>
        );
      } else {
        const otherCount = from.length - 2;
        return (
          <>
            <span className={styles.username}>{from[0]?.username}</span>,{" "}
            <span className={styles.username}>{from[1]?.username}</span>, and{" "}
            <span className={styles.username}>
              {otherCount} other{otherCount > 1 ? "s" : ""}
            </span>
          </>
        );
      }
    } else {
      return <span className={styles.username}>{from[0]?.username ?? ""}</span>;
    }
  } else {
    return <span className={styles.username}>{from.username}</span>;
  }
};

const getFromProfilesFound = (notification: AnyNotification) => {
  if (
    notification.type === NotificationType.CoralCommentApproved ||
    notification.type === NotificationType.CycloneFanPostApproved ||
    notification.type === NotificationType.CycloneReplyApproved
  ) {
    return [];
  } else {
    const from = getFromProfileForNotification(notification);
    // some notifications, like rec summaries, can have multiple from profiles
    const fromProfiles = Array.isArray(from) ? from : from ? [from] : [];

    const filteredFromProfiles = fromProfiles.filter(
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (profile) => !!profile,
    );
    // find the first profile that matches the notification source for each from profile
    return filteredFromProfiles
      .map((profile) =>
        profile.profiles?.find(
          (profile) =>
            profile.type.toString() === notification.source.toString(),
        ),
      )
      .filter((profile) => profile !== undefined);
  }
};

const getNotificationImage = (
  notification: AnyNotification,
  showCommunityLogo: boolean,
  avatarSize: AvatarSize,
  fromProfilesFound: ExternalUserProfile[],
) => {
  if (
    notification.type === NotificationType.CoralCommentApproved ||
    notification.type === NotificationType.CycloneFanPostApproved ||
    notification.type === NotificationType.CycloneReplyApproved
  ) {
    return (
      <CommunityLogo notification={notification} size="large" needsWrapper />
    );
  } else {
    const avatarProfile =
      fromProfilesFound.length > 0 ? fromProfilesFound[0]! : null;
    return avatarProfile ? (
      <Avatar
        fromProfile={avatarProfile}
        showCommunityLogo={showCommunityLogo}
        notification={notification}
        avatarSize={avatarSize}
      />
    ) : null;
  }
};

interface NotificationProps {
  notification: AnyNotification;
  showCommunityLogo?: boolean;
  avatarSize?: AvatarSize;
  handleNotificationClick?: (notification: AnyNotification) => Promise<void>;
  className?: string;
}

/**
 * A single notification component.
 */
const Notification: FunctionComponent<NotificationProps> = observer(
  function Notification({
    notification,
    showCommunityLogo = true,
    avatarSize = "large",
    handleNotificationClick,
    className,
  }) {
    const { cycloneCommunitiesById } = useCommunityContext();
    const rootAncestor = getRootAncestorForNotification(notification);
    const text = getTextForNotification(notification, rootAncestor);

    const snippet = getSnippetForNotification(notification);

    const fromProfilesFound = getFromProfilesFound(notification);

    const notificationImage: React.ReactElement | null = getNotificationImage(
      notification,
      showCommunityLogo,
      avatarSize,
      fromProfilesFound,
    );

    const fromProfileText = getFromProfileText(
      fromProfilesFound,
      notification.type,
    );

    const url = getUrlForNotification(notification, cycloneCommunitiesById);

    const timestamp =
      "updatedAt" in notification
        ? notification.updatedAt
        : notification.createdAt;

    // Handles notification click or context menu (right click) to open in new tab
    const handleLinkEvent = useCallback(() => {
      if (handleNotificationClick) {
        void handleNotificationClick(notification);
      }
    }, [handleNotificationClick, notification]);

    return (
      <a
        href={url}
        onClick={handleLinkEvent}
        onContextMenu={handleLinkEvent}
        className={clsx(styles.notificationContainer, className)}
        tabIndex={0}
      >
        <div className={styles.notificationTop}>
          <div className={styles.notificationFromProfile}>
            {notificationImage && notificationImage}
            <div className={styles.notificationMessage}>
              {fromProfileText} {text}
            </div>
          </div>

          {!notification.read && <span className={styles.unreadIndicator} />}
        </div>
        <div className={styles.notificationBottom}>
          {snippet && snippet !== "" && (
            <span className={styles.snippet}>{snippet}</span>
          )}
          <Timestamp timestamp={timestamp} format="notifications" />
        </div>
      </a>
    );
  },
);

export default Notification;
